import { useEffect, useState, useRef } from "react";
import { Users, Circle } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import { tCrm, crmApi, logDevError } from "../../lib/crm";

/**
 * Top-bar widget (admin only) showing how many CRM agents are currently online
 * and a popover with their names + last_seen time. Polls /agents/presence every
 * 30 seconds.
 */
export default function AgentsPresenceBadge() {
  const { language } = useLanguage();
  const t = (k) => tCrm(language, k);
  const [presence, setPresence] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = () => {
    crmApi
      .get("/agents/presence")
      .then((r) => setPresence(r.data || []))
      .catch((err) => logDevError("AgentsPresenceBadge.load", err));
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, []);

  // Close popover on outside click
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const onlineCount = presence.filter((p) => p.is_online).length;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="presence-badge"
        title={t("presence.title")}
        className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-white/10 hover:border-lime-400/40 hover:bg-white/[0.03] transition"
      >
        <Users className="w-3.5 h-3.5 text-white/60" />
        <span className="relative flex h-2 w-2">
          <span
            className={`absolute inline-flex h-full w-full rounded-full ${
              onlineCount > 0 ? "bg-emerald-400 animate-ping" : ""
            } opacity-60`}
          />
          <span
            className={`relative inline-flex rounded-full h-2 w-2 ${
              onlineCount > 0 ? "bg-emerald-400" : "bg-white/30"
            }`}
          />
        </span>
        <span className="text-xs font-semibold text-white mono">
          {onlineCount}
          <span className="text-white/40">/{presence.length}</span>
        </span>
      </button>

      {open && (
        <div
          data-testid="presence-popover"
          className="absolute right-0 mt-2 w-72 glass-strong rounded-xl border border-white/10 shadow-xl p-2 z-50"
        >
          <div className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-white/40 mono flex items-center justify-between">
            <span>{t("presence.title")}</span>
            <span className="text-emerald-300">
              {onlineCount} {t("presence.online")}
            </span>
          </div>
          {presence.length === 0 ? (
            <div className="px-3 py-3 text-xs text-white/40">{t("presence.empty")}</div>
          ) : (
            <ul className="max-h-72 overflow-y-auto">
              {presence.map((p) => (
                <li
                  key={p.id}
                  data-testid={`presence-row-${p.id}`}
                  className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-white/[0.03]"
                >
                  <Circle
                    className={`w-2 h-2 shrink-0 ${
                      p.is_online ? "fill-emerald-400 text-emerald-400" : "fill-white/20 text-white/20"
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-white truncate">{p.full_name}</div>
                    <div className="text-[10px] text-white/40 truncate">{p.email}</div>
                  </div>
                  <div className="text-[10px] text-white/40 mono shrink-0">
                    {p.is_online ? t("presence.now") : formatLastSeen(p.last_seen_at, language)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function formatLastSeen(iso, language) {
  if (!iso) return language === "es" ? "nunca" : "never";
  const dt = new Date(iso);
  const seconds = Math.floor((Date.now() - dt.getTime()) / 1000);
  if (seconds < 60) return language === "es" ? "hace seg." : "secs ago";
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return language === "es" ? `hace ${mins} min` : `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return language === "es" ? `hace ${hrs}h` : `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return language === "es" ? `hace ${days}d` : `${days}d ago`;
}
